// ============================================================================
// ImportPreviewModal — Modal de Preview + Aprovação para Importação de Dados
//
// Recebe JSON canônico, valida contra schema Zod, renderiza preview,
// e permite ao operador aprovar ou cancelar a importação.
// ============================================================================

import React, { useMemo, useState } from "react";
import { X, AlertTriangle, Check, Loader } from "lucide-react";

// ── FORMATADORES ─────────────────────────────────────────────────────────────
const formatLabel = (key) => {
  const map = {
    name: "Nome",
    cpf: "CPF",
    rg: "RG",
    email: "E-mail",
    phonePrimary: "Telefone Principal",
    phone: "Telefone",
    birthDate: "Data de Nascimento",
    nationality: "Nacionalidade",
    naturalness: "Naturalidade",
    civilStatus: "Estado Civil",
    maritalRegime: "Regime de Bens",
    spouseName: "Nome do Cônjuge",
    spouseCPF: "CPF do Cônjuge",
    profession: "Profissão",
    socialName: "Nome Social",
    motherName: "Nome da Mãe",
    fatherName: "Nome do Pai",
    pronoun: "Pronome",
    genderIdentity: "Identidade de Gênero",
    grammaticalGender: "Gênero Gramatical",
    street: "Logradouro",
    number: "Número",
    complement: "Complemento",
    neighborhood: "Bairro",
    city: "Cidade",
    state: "UF",
    zipCode: "CEP",
    companyName: "Razão Social",
    tradeName: "Nome Fantasia",
    cnpj: "CNPJ",
    type: "Tipo de Cliente",
    isProBono: "Pro Bono",
    hasLegalAid: "Assistência Judiciária",
    isFullDependent: "Dependente Total",
    livesWithAnchor: "Reside com Responsável",
    species: "Espécie",
    breed: "Raça",
    biologicalSex: "Sexo Biológico",
    etimatedAge: "Idade Estimada",
    microchip: "Microchip",
    rga: "RGA",
    deceasedName: "Nome do Falecido",
    dateOfDeath: "Data do Óbito",
    mothersName: "Nome da Mãe (Gestante)",
    conceptionDate: "Data da Concepção",
    expectedBirthDate: "Previsão de Nascimento",
    matriculaPrisional: "Matrícula Prisional",
    prisonUnit: "Unidade Prisional",
    executionRegime: "Regime de Execução",
    representativeType: "Tipo de Representação",
    guardianshipProcessNumber: "Processo de Tutela",
    isDigitalNative: "Nativo Digital",
    emergencyContact: "Contato de Emergência",
  };
  return map[key] || key;
};

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getModuleTitle = (module) => {
  const map = {
    clientes: "Cliente",
    contratos: "Contrato",
    procuracaoes: "Procuração",
    declaracoes: "Declaração",
    consentimentos: "Consentimento",
    financas_pessoais: "Finança Pessoal",
  };
  return map[module] || module;
};

// ── COMPONENTE DE PREVIEW DE CAMPO ──────────────────────────────────────────
const FieldPreview = ({ label, value }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
    <div className="w-40 flex-shrink-0 text-sm font-medium text-gray-600">
      {label}
    </div>
    <div className="flex-1 text-sm text-gray-900 break-words">
      {formatValue(label, value)}
    </div>
  </div>
);

const SectionPreview = ({ title, children }) => (
  <div className="mb-4">
    <h4 className="text-xs font-bold text-brand-blue uppercase tracking-wider mb-2 bg-gray-50 px-3 py-1.5 rounded">
      {title}
    </h4>
    {children}
  </div>
);

// ── EXTRAIR CAMPOS PARA PREVIEW ──────────────────────────────────────────────
const extractPreviewFields = (data) => {
  if (!data) return {};

  const fields = {};
  const qual = data.qualification || {};
  const contact = data.contact || {};
  const location = data.location || {};
  const address = location.address || {};
  const financial = data.financial || {};

  // Tipo de cliente
  fields.tipo = data.type || "—";

  // Campos por tipo de pessoa
  if (data.type === "Pessoa Física") {
    const phys = qual.physical || {};
    Object.assign(fields, {
      nome: phys.name,
      cpf: phys.cpf,
      rg: phys.rg,
      "data de nascimento": phys.birthDate,
      nacionalidade: phys.nationality,
      naturalidade: phys.naturalness,
      "estado civil": phys.civilStatus,
      profissão: phys.profession,
      "nome da mãe": phys.motherName,
      "nome do pai": phys.fatherName,
      email: contact.email,
      telefone: contact.phonePrimary,
    });
  } else if (data.type === "Pessoa Jurídica") {
    const leg = qual.legal || {};
    Object.assign(fields, {
      "razão social": leg.companyName,
      "nome fantasia": leg.tradeName,
      cnpj: leg.cnpj,
      email: contact.email,
      telefone: contact.phonePrimary,
    });
  } else {
    // Outros tipos — mostrar o primeiro nome disponível
    const firstQual = Object.values(qual).find((v) => v?.name);
    if (firstQual?.name) fields.nome = firstQual.name;
    if (contact.email) fields.email = contact.email;
    if (contact.phonePrimary) fields.telefone = contact.phonePrimary;
  }

  // Endereço
  if (address.street || address.city) {
    fields.endereço = [address.street, address.number, address.complement]
      .filter(Boolean)
      .join(", ");
    fields.bairro = address.neighborhood;
    fields.cidade = [address.city, address.state].filter(Boolean).join("/");
    fields.cep = address.zipCode;
  }

  // Financeiro
  if (financial.isProBono) fields["pro bono"] = "Sim";
  if (financial.hasLegalAid) fields["assistência judiciária"] = "Sim";

  return fields;
};

// ── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
export default function ImportPreviewModal({
  isOpen,
  onClose,
  payload,
  moduleName,
  validator,
}) {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  // Validar dados
  const previewData = useMemo(() => {
    if (!payload?.data) return null;
    setValidationErrors([]);

    // Validar contra schema se fornecido
    if (validator) {
      const validation = validator(payload.data);
      if (!validation.valid) {
        setValidationErrors(validation.errors || []);
        return null;
      }
      return validation.data;
    }

    return payload.data;
  }, [payload, validator]);

  const previewFields = useMemo(
    () => extractPreviewFields(previewData),
    [previewData],
  );

  const handleConfirm = async () => {
    setImporting(true);
    try {
      const { importData } = await import("../services/ImportRepository");
      const importResult = await importData(payload);
      setResult(importResult);
    } catch (error) {
      setResult({
        success: false,
        error: "IMPORT_ERROR",
        message: error.message,
      });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setValidationErrors([]);
    setImporting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-brand-blue/10 flex items-center justify-center">
              <i className="fas fa-file-import text-brand-blue text-sm" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-800">
                Importar {getModuleTitle(moduleName || "")}
              </h2>
              <p className="text-[11px] text-gray-500">
                Revise os dados antes de confirmar
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={importing}
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Erros de validação */}
          {validationErrors.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-xs font-bold text-red-700 uppercase">
                  Erros de validação
                </span>
              </div>
              <ul className="space-y-1">
                {validationErrors.map((err, idx) => (
                  <li key={idx} className="text-[11px] text-red-600 flex gap-2">
                    <span className="font-mono text-red-400">{err.path}</span>
                    <span>{err.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Resultado da importação */}
          {result && (
            <div
              className={`mb-4 p-4 rounded-lg border ${
                result.success
                  ? "bg-green-50 border-green-200"
                  : "bg-red-50 border-red-200"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                {result.success ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                )}
                <span
                  className={`text-sm font-bold ${
                    result.success ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {result.success
                    ? "Importado com sucesso!"
                    : "Erro na importação"}
                </span>
              </div>
              {result.success ? (
                <p className="text-xs text-green-600">
                  ID: {result.id} | Ação: {result.action}
                </p>
              ) : (
                <p className="text-xs text-red-600">{result.message}</p>
              )}
            </div>
          )}

          {/* Preview dos dados */}
          {!result && validationErrors.length === 0 && previewFields && (
            <div className="space-y-1">
              {Object.entries(previewFields).map(([key, value]) => (
                <FieldPreview
                  key={key}
                  label={formatLabel(key)}
                  value={value}
                />
              ))}
            </div>
          )}

          {/* Metadados */}
          {payload?.metadata && !result && (
            <SectionPreview title="Metadados da Importação">
              <FieldPreview label="Agente" value={payload.metadata.agent} />
              <FieldPreview
                label="Operador"
                value={payload.metadata.operator}
              />
              <FieldPreview
                label="Gerado em"
                value={payload.metadata.generated_at}
              />
              <FieldPreview label="Schema" value={payload.schema_version} />
            </SectionPreview>
          )}
        </div>

        {/* FOOTER */}
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between">
          <span className="text-[11px] text-gray-400">
            {previewFields
              ? `${Object.keys(previewFields).length} campos`
              : "Dados inválidos"}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={importing}
            >
              {result ? "Fechar" : "Cancelar"}
            </button>
            {!result && validationErrors.length === 0 && previewFields && (
              <button
                onClick={handleConfirm}
                disabled={importing}
                className="px-4 py-2 text-sm font-bold text-white bg-brand-blue hover:bg-brand-blue-darker rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {importing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Aprovar e Importar
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
